import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileText, Mail, Send, ShieldAlert, Stamp, X } from 'lucide-react';
import { dateiAusgeben } from '../../services/dateiAusgabe';
import { istNativeApp } from '../../services/nativePlatform';
import { fetchPlanungshinweise } from '../../services/planungsphaseService';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import type { PlanungshinweisPdfOptionen } from '../../services/pdf/generatorPlanungshinweis';
import type { EmpfaengerQuellen, EmpfaengerZuordnung } from './empfaenger';
import { baueHinweisPdf, firmaAusKontext } from './hinweisPdf';
import {
    fuehreHinweisLauf,
    hinweisDateiname,
    hinweisEmpfaengerVorbelegung,
    hinweisFassungRef,
    hinweisMailBetreff,
    hinweisMailText,
    hinweisTitel,
    hinweisUngespeichert,
    kopienAus,
    pruefeHauptempfaenger,
    stempelNachholen,
    type HinweisLaufSchritte,
    type VersandStand,
} from './hinweisVersandVorbelegung';
import { pdfAlsBlob, type PdfKontext } from './planungIntegration';
import { heuteLokalIso, HINWEIS_ART_LABEL, pruefeHinweis } from './planungLogic';
import {
    bedenkenStempelMeldung,
    nichtZugestelltMeldung,
    stempleBedenkenanzeige,
    versende,
    versendeBedenkenanzeige,
    type BedenkenanzeigeErgebnis,
    type BedenkenStempel,
    type VersandAuftrag,
    type VersandEmpfaenger,
    type VersandErgebnis,
} from './planungVersand';
import type { HinweisArt, NeuerPlanungshinweis, Planungsfrage, Planungshinweis } from './types';
import { datumDeutsch } from './unterlagenLogic';

/**
 * Was der Editor fuer „PDF ansehen" und „Versenden …" braucht. Fehlt der
 * Kontext, bleiben beide Knoepfe aus und sagen warum — der Editor laeuft
 * dann wie bisher (Entwurf speichern, Stempel setzen).
 */
export interface HinweisVersandKontext {
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    /** Rohe Stammdaten fuer den Briefkopf (`buildCompanyForPdf` laeuft in `hinweisPdf.ts`). */
    pdf: PdfKontext;
    /** Fragen des Projekts, damit zu jedem `frage_codes`-Eintrag der Fragetext steht. */
    fragen: ReadonlyArray<Pick<Planungsfrage, 'code' | 'frage'>>;
    /** Quellen fuer die Vorbelegung des Bauherrn (Projektstammdaten, Verteiler). */
    empfaengerQuellen?: EmpfaengerQuellen | null;
    /** Empfaenger-Zuordnung der Planungsphase (`planungsphasen.empfaenger`). */
    empfaengerZuordnung?: EmpfaengerZuordnung | null;
    /** Gesetzt, wenn aus diesem Projekt nichts hinausgeschickt werden darf
     *  (Uebungsprojekt) — „PDF ansehen" bleibt, der Versand nennt den Grund. */
    versandGesperrt?: string | null;
}

interface Props {
    /** Bestehender Hinweis oder Entwurf. */
    entwurf: NeuerPlanungshinweis;
    /** Gesetzt, wenn ein bestehender Satz bearbeitet wird. */
    bestand?: Planungshinweis | null;
    isReadOnly: boolean;
    onChange: (e: NeuerPlanungshinweis) => void;
    onSpeichern: () => void;
    /** Nur der Stempel `versendet_am` — fuer Versand AUSSERHALB der App. */
    onVersenden: () => void;
    onAbbrechen: () => void;
    laeuft: boolean;
    /** Kontext fuer PDF und Versand aus der App (Stufe 4). Optional, siehe `HinweisVersandKontext`. */
    versand?: HinweisVersandKontext | null;
    /** Nach erfolgreichem Versand aus der App: der gestempelte Satz. */
    onVersendet?: (h: Planungshinweis) => void;
}

const ART_STIL: Record<HinweisArt, { farbe: string; icon: React.ReactNode; erklaerung: string }> = {
    hinweis: {
        farbe: 'bg-slate-50 border-slate-200 text-slate-700',
        icon: <CheckCircle2 className="w-4 h-4" />,
        erklaerung: 'Beratung im Regelbetrieb. Kein förmlicher Enthaftungstatbestand — dafür ohne Pflichtfelder.',
    },
    bedenken: {
        farbe: 'bg-amber-50 border-amber-300 text-amber-800',
        icon: <AlertTriangle className="w-4 h-4" />,
        erklaerung: 'Förmliche Bedenkenanzeige. Nur mit allen Pflichtfeldern und an den Bauherrn gerichtet entfaltet sie Wirkung.',
    },
    gefahr: {
        farbe: 'bg-rose-50 border-rose-300 text-rose-800',
        icon: <ShieldAlert className="w-4 h-4" />,
        erklaerung: 'Gefahr im Verzug. Enthält ausdrücklich das Verlangen, die Arbeiten einstellen zu lassen.',
    },
};

const Feld: React.FC<{
    label: string;
    hinweis?: string;
    pflicht?: boolean;
    children: React.ReactNode;
}> = ({ label, hinweis, pflicht, children }) => (
    <div>
        <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
            {label}{pflicht && <span className="text-rose-500 ml-1">Pflicht</span>}
        </label>
        {children}
        {hinweis && <span className="block text-[11px] text-slate-400 mt-1 leading-snug">{hinweis}</span>}
    </div>
);

/** Rueckmeldung des Versands bzw. der Vorschau — im Dialog, nie als Browser-Dialog. */
interface Meldung {
    art: 'ok' | 'warnung' | 'fehler';
    text: string;
    /** Mail ist raus, der Stempel fehlt — der Knopf holt ihn mit DIESEM Zeitpunkt nach. */
    stempelNachholen?: { versendetAm: string };
}

/** Objekt-URL erst spaet freigeben: manche Browser lesen den Blob erst nach dem Oeffnen. */
const URL_FREIGABE_MS = 40_000;

/**
 * Erfassung von Hinweis, Bedenkenanzeige und Gefahrenanzeige.
 *
 * Der Sinn der Maske liegt in dem, was sie ERZWINGT. Die Rechtsprechung zu
 * § 4 Abs. 3 VOB/B verlangt bei der Bedenkenanzeige den konkreten
 * Sachverhalt, die konkrete Gefaehrdung und vor allem die konkreten
 * NACHTEILIGEN FOLGEN; der pauschale Hinweis enthaftet nicht, und eine
 * Anzeige nur an den Architekten regelmaessig ebenso wenig. Genau diese
 * Punkte stehen als eigene Felder da und werden vor dem Versand geprueft —
 * eine Freitextmaske haette dieselbe Wirkung wie ein leeres Blatt Papier.
 *
 * ═══ ZWEI WEGE ZUM STEMPEL ════════════════════════════════════════════════
 * Ein Entwurf ist kein Versand; erst `versendet_am` deckt eine offene
 * A-Frage im Freigabe-Gate (`pruefeFreigabe`). Den Stempel gibt es auf zwei
 * Wegen:
 *   - „Versenden …" (Stufe 4.1): PDF erzeugen, in die Ablage, per Mail an
 *     den Bauherrn, DANN stempeln — die Beweiskette liegt in der App.
 *   - „Als versendet festhalten": nur der Stempel, fuer einen Versand
 *     ausserhalb der App (Brief, eigenes Mailprogramm). Ablage und
 *     Mailnachweis fehlen dann; der Knopf sagt das dazu.
 * Versendet und gedruckt wird immer der GESPEICHERTE Satz: das Blatt
 * entsteht aus dem, was die Akte traegt — nicht aus einem Entwurf, der noch
 * anders aussieht. Mit ungespeicherten Aenderungen sind „PDF ansehen" und
 * „Jetzt versenden" gesperrt und sagen „erst speichern".
 *
 * ═══ KEIN ZWEITER VERSAND AN ALLE ═════════════════════════════════════════
 * Sobald ein Lauf EINEN Empfaenger erreicht hat, haelt `versandStand` fest,
 * wer erreicht wurde und wann. Danach gibt es fuer diesen Satz nur noch
 * „Stempel nachholen" (mit dem Zeitpunkt des ersten Erfolgs) und „An die
 * nicht erreichten erneut senden" — ein Klick auf „Jetzt versenden" nach
 * einem Stempelfehler schickte sonst dieselbe Anzeige noch einmal an alle
 * (Codex-Review Stufe 4). Die Reihenfolge eines Laufs (wann gestempelt
 * wird, wann nicht) steht in `fuehreHinweisLauf` — pur und getestet; der
 * Editor reicht nur die Schritte des Versandwegs hinein.
 */
export const HinweisEditor: React.FC<Props> = ({
    entwurf, bestand, isReadOnly, onChange, onSpeichern, onVersenden, onAbbrechen, laeuft,
    versand, onVersendet,
}) => {
    const [versandVersucht, setVersandVersucht] = useState(false);
    const maengel = useMemo(() => pruefeHinweis(entwurf as Partial<Planungshinweis>), [entwurf]);
    const stil = ART_STIL[entwurf.art] || ART_STIL.hinweis;

    // Versand aus der App: Dialog, Vorbelegung, Lauf und Rueckmeldung.
    const [versandOffen, setVersandOffen] = useState(false);
    const [mailBetreff, setMailBetreff] = useState('');
    const [mailText, setMailText] = useState('');
    const [kopien, setKopien] = useState('');
    const [versandLaeuft, setVersandLaeuft] = useState(false);
    const [meldung, setMeldung] = useState<Meldung | null>(null);
    // Nach erfolgreichem Versand ohne `onVersendet`-Anbindung: der Editor
    // zeigt den Stempel selbst, bis der Aufrufer neu laedt.
    const [versendetLokal, setVersendetLokal] = useState<string | null>(null);
    // Doppelklick-Riegel wie `mitLauf` im PlanungModul: der Zustand greift
    // erst nach dem naechsten Zeichnen.
    const versandRef = useRef(false);
    // Was ein Lauf fuer diesen Satz hinterlassen hat (erreichte Adressen,
    // erster Zeitpunkt, nicht erreichte Empfaenger) — die Sperre gegen einen
    // zweiten Versand an alle. Gilt nur fuer den Satz, fuer den er entstand.
    const [versandStand, setVersandStand] = useState<VersandStand | null>(null);

    const versendetAm = (bestand && bestand.versendet_am) || versendetLokal;
    const bereitsVersendet = !!versendetAm;
    const gesperrt = isReadOnly || laeuft || versandLaeuft;
    const kontextFehlt = !versand;
    const standGilt = versandStand && bestand && versandStand.hinweisId === bestand.id ? versandStand : null;
    /** Entwurf weicht vom gespeicherten Satz ab (oder es gibt noch keinen) — dann wird weder gedruckt noch versendet. */
    const ungespeichert = useMemo(() => hinweisUngespeichert(entwurf, bestand), [entwurf, bestand]);

    const setze = (feld: keyof NeuerPlanungshinweis, wert: unknown) =>
        onChange({ ...entwurf, [feld]: wert } as NeuerPlanungshinweis);

    // ─── Empfaenger vorbelegen: Bauherr aus Zuordnung bzw. Stammdaten ────
    const vorbelegung = useMemo(
        () => hinweisEmpfaengerVorbelegung(entwurf, versand?.empfaengerZuordnung, versand?.empfaengerQuellen),
        [entwurf, versand],
    );
    const vorbelegungVerfuegbar = vorbelegung.herkunft === 'zuordnung' || vorbelegung.herkunft === 'stammdaten';

    // Ein NEUER Entwurf bekommt den Bauherrn einmal vorbelegt — sichtbar,
    // aenderbar, gespeichert wird erst per Knopf. Ein bestehender Satz mit
    // leerem Empfaenger wird nicht still umgeschrieben; dort steht der
    // Uebernehmen-Knopf. `vorbelegt` merkt Name und Grund fuer den Hinweis
    // unter dem Feld — nach dem Vorbelegen saehe `vorbelegung` den Namen
    // sonst als „am Hinweis eingetragen".
    const vorbelegtRef = useRef(false);
    const [vorbelegt, setVorbelegt] = useState<{ name: string; grund: string } | null>(null);
    useEffect(() => {
        if (vorbelegtRef.current || bestand || isReadOnly) return;
        if ((entwurf.empfaenger_name || '').trim() || !vorbelegungVerfuegbar) return;
        vorbelegtRef.current = true;
        setVorbelegt({ name: vorbelegung.name, grund: vorbelegung.grund });
        onChange({ ...entwurf, empfaenger_name: vorbelegung.name, empfaenger_email: vorbelegung.email });
    }, [bestand, isReadOnly, entwurf, vorbelegung, vorbelegungVerfuegbar, onChange]);

    const bauherrUebernehmen = () => {
        if (gesperrt || bereitsVersendet || !vorbelegungVerfuegbar) return;
        onChange({ ...entwurf, empfaenger_name: vorbelegung.name, empfaenger_email: vorbelegung.email });
    };

    // ─── Stempel ohne App-Versand ────────────────────────────────────────
    const stempeln = () => {
        setVersandVersucht(true);
        if (maengel.length) return;
        onVersenden();
    };

    // ─── PDF ─────────────────────────────────────────────────────────────
    const projekt = { projektName: versand?.projektName, projektNummer: versand?.projektNummer };
    const projektKennung = (versand?.projektNummer || versand?.projektName || '').trim();

    const pdfOptionen = (datumIso?: string): PlanungshinweisPdfOptionen => ({
        projektName: versand?.projektName,
        projektNummer: versand?.projektNummer,
        bauvorhabenAdresse: versand?.projektAdresse,
        fragen: versand?.fragen,
        datumIso,
    });

    const ERST_SPEICHERN = 'Erst speichern — gedruckt und versendet wird nur der gespeicherte Satz.';

    /** Das Blatt aus dem GESPEICHERTEN Satz — nie aus einem ungespeicherten Entwurf. */
    const pdfAnsehen = async () => {
        if (gesperrt || !versand) return;
        setMeldung(null);
        if (!bestand || ungespeichert) { setMeldung({ art: 'warnung', text: ERST_SPEICHERN }); return; }
        const doc = baueHinweisPdf(bestand, versand.pdf, pdfOptionen());
        if (doc.status === 'fehler') { setMeldung({ art: 'fehler', text: doc.fehler }); return; }
        const blob = pdfAlsBlob(doc.wert);
        if (blob.status === 'fehler') { setMeldung({ art: 'fehler', text: blob.fehler }); return; }
        const dateiname = hinweisDateiname(bestand, projektKennung, heuteLokalIso());
        try {
            if (istNativeApp()) {
                await dateiAusgeben(blob.wert, dateiname);
                return;
            }
            const url = URL.createObjectURL(blob.wert);
            // Ohne `noopener`: damit liefert `window.open` immer null, und der
            // Popup-Blocker waere nicht vom Erfolg zu unterscheiden. Die
            // Objekt-URL ist same-origin, ein Opener-Zugriff kein Risiko.
            const fenster = window.open(url, '_blank');
            // Popup-Blocker: dann wenigstens als Download ausgeben, statt stumm zu bleiben.
            if (!fenster) await dateiAusgeben(blob.wert, dateiname);
            window.setTimeout(() => URL.revokeObjectURL(url), URL_FREIGABE_MS);
        } catch (e) {
            setMeldung({ art: 'fehler', text: `Das PDF konnte nicht geöffnet werden: ${e instanceof Error ? e.message : String(e)}` });
        }
    };

    // ─── Versand aus der App ─────────────────────────────────────────────
    const oeffneVersand = () => {
        if (gesperrt || !versand) return;
        setVersandVersucht(true);
        setMeldung(null);
        const satz = bestand || entwurf;
        setMailBetreff(hinweisMailBetreff(satz, projekt));
        setMailText(hinweisMailText(satz, projekt, {
            name: entwurf.erstellt_von_name,
            firma: firmaAusKontext(versand.pdf),
        }));
        // Nur Eintraege, die wie Adressen aussehen — Namen im Verteiler sind kein Mailempfaenger.
        setKopien((entwurf.verteiler || []).filter(v => v.includes('@')).join('\n'));
        setVersandOffen(true);
    };

    const kopienErgebnis = useMemo(() => kopienAus(kopien), [kopien]);

    /** Alles, was einen Versandlauf sperrt — sichtbar aufgelistet, kein stummer Knopf.
     *  Die Sperre nach einem Teilerfolg (`standGilt`) steht NICHT hier: sie
     *  sperrt nur „an alle", nicht den Lauf an die nicht erreichten. */
    const versandBlocker = useMemo((): string[] => {
        const liste: string[] = [];
        if (versand?.versandGesperrt) liste.push(versand.versandGesperrt);
        if (!bestand) liste.push('Zuerst „Als Entwurf speichern" — versendet wird der gespeicherte Satz mit seiner Nummer.');
        else if (ungespeichert) liste.push('Es gibt ungespeicherte Änderungen. Bitte zuerst speichern, damit Blatt und Akte denselben Stand tragen.');
        liste.push(...maengel);
        const empf = pruefeHauptempfaenger(entwurf.empfaenger_name, entwurf.empfaenger_email);
        if (empf) liste.push(empf);
        liste.push(...kopienErgebnis.fehler);
        if (!mailBetreff.trim()) liste.push('Der Betreff der E-Mail fehlt.');
        return liste;
    }, [versand?.versandGesperrt, bestand, ungespeichert, entwurf, maengel, kopienErgebnis, mailBetreff]);

    /** Der Satz ist gestempelt — Editor und Aufrufer erfahren es. Den Dialog schliesst der Aufrufer, wenn nichts mehr offen ist. */
    const uebernimmStempel = (versendetAmIso: string) => {
        setVersendetLokal(versendetAmIso);
        if (onVersendet && bestand) onVersendet({ ...bestand, versendet_am: versendetAmIso });
    };

    /** Die echten Schritte des Versandwegs fuer `fuehreHinweisLauf` und `stempelNachholen`. */
    const schritteFuer = (satz: Planungshinweis): HinweisLaufSchritte => ({
        versende: a => versende(a),
        versendeMitStempel: a => versendeBedenkenanzeige(satz, a),
        stemple: am => stempleBedenkenanzeige(satz.id, am),
        // Bei `schon_versendet` zaehlt der gespeicherte Zeitpunkt — ein anderer
        // Lauf kann einen frueheren gesetzt haben. Kein Einzel-Lesezugriff im
        // Service, deshalb ueber die Liste der Planungsphase.
        leseVersendetAm: async () => {
            const gelesen = (await fetchPlanungshinweise(satz.planung_id)).find(h => h.id === satz.id);
            return gelesen && gelesen.versendet_am ? gelesen.versendet_am : null;
        },
    });

    /**
     * Ergebnis des Versandwegs in eine Meldung uebersetzen. Der Fehlerpfad
     * „Mail raus, Stempel gescheitert" bekommt den Knopf „Stempel nachholen"
     * (Plan 4.1: nie still). Teilerfolge und Ablagehinweise stehen mit im
     * Satz — nichts davon verschwindet hinter einem gruenen Haken.
     */
    const deuteErgebnis = (ergebnis: BedenkenanzeigeErgebnis): Meldung => {
        const { versand: v, stempel } = ergebnis;
        if (v.status === 'fehler' || !v.versendetAn.length) {
            return {
                art: 'fehler',
                text: ['Es wurde nichts versendet.', ...v.fehler, ...v.hinweise].join(' '),
            };
        }
        const stempelSatz = bedenkenStempelMeldung(stempel);
        if (!v.hauptempfaengerErreicht) {
            // Nur Kopien erreicht: der Bauherr fehlt, nichts gestempelt — das
            // ist kein halber Erfolg und bekommt keinen „nachholen"-Knopf
            // (es gibt keinen Zeitpunkt). Der Satz von S4-a nennt es beim Namen.
            const kern = stempelSatz.fehler
                || nichtZugestelltMeldung(v)
                || 'Der Hauptempfänger (Bauherr) wurde nicht erreicht — nichts gestempelt.';
            return { art: 'warnung', text: [kern, ...v.fehler, ...v.hinweise].join(' ') };
        }
        const haupt = v.hauptempfaenger ? ` (Bauherr: ${v.hauptempfaenger.email})` : '';
        const teile: string[] = [
            v.status === 'versendet'
                ? `E-Mail versendet an ${v.versendetAn.join(', ')}${haupt}.`
                : `An den Bauherrn zugestellt${haupt}; nicht erreicht: ${v.fehler.join(' ')}`,
        ];
        if (stempelSatz.text) teile.push(stempelSatz.text);
        teile.push(...v.hinweise);
        if (stempelSatz.fehler) {
            teile.push(stempelSatz.fehler);
            return {
                art: 'warnung',
                text: teile.join(' '),
                stempelNachholen: stempel.versendetAm ? { versendetAm: stempel.versendetAm } : undefined,
            };
        }
        return {
            art: v.status === 'versendet' && !v.hinweise.length ? 'ok' : 'warnung',
            text: teile.join(' '),
        };
    };

    /**
     * Meldung eines Wiederholungslaufs an Kopien (Bauherr schon erreicht):
     * kein neuer Stempel, der erste Zeitpunkt bleibt — der Versandweg nennt
     * hier die erste Kopie „Hauptempfaenger", das darf nicht als Bauherr
     * gelesen werden. Ein Vorab-Stempel dieses Laufs steht mit im Satz.
     */
    const deuteWiederholung = (v: VersandErgebnis, vorabStempel: BedenkenStempel | null): Meldung => {
        if (!v.versendetAn.length) {
            return { art: 'fehler', text: ['Die Wiederholung hat niemanden erreicht.', ...v.fehler, ...v.hinweise].join(' ') };
        }
        const teile: string[] = [`Erneut versendet an ${v.versendetAn.join(', ')}.`];
        if (v.fehler.length) teile.push(`Nicht erreicht: ${v.fehler.join(' ')}`);
        teile.push('Das erste Versanddatum bleibt stehen — für den Zugangsnachweis zählt der erste Versand.');
        if (vorabStempel) {
            const satz = bedenkenStempelMeldung(vorabStempel);
            if (satz.text) teile.push(satz.text);
        }
        teile.push(...v.hinweise);
        return { art: v.fehler.length || v.hinweise.length ? 'warnung' : 'ok', text: teile.join(' ') };
    };

    /**
     * EIN Versandlauf an die genannten Empfaenger: Blatt aus dem gespeicherten
     * Satz, Auftrag, dann `fuehreHinweisLauf` mit den echten Schritten des
     * Versandwegs; Stand merken, Stempel uebernehmen. Gemeinsamer Kern von
     * „Jetzt versenden" (alle) und „erneut senden" (nur die nicht erreichten).
     * Der Dialog bleibt offen, solange etwas nachzuholen ist.
     */
    const fuehreVersandAus = async (empfaenger: VersandEmpfaenger[]) => {
        if (gesperrt || !versand || !bestand || versandBlocker.length || versandRef.current) return;
        if (!empfaenger.length) return;
        versandRef.current = true;
        setVersandLaeuft(true);
        setMeldung(null);
        try {
            const datumIso = heuteLokalIso();
            const doc = baueHinweisPdf(bestand, versand.pdf, pdfOptionen(datumIso));
            if (doc.status === 'fehler') throw new Error(doc.fehler);
            const blob = pdfAlsBlob(doc.wert);
            if (blob.status === 'fehler') throw new Error(blob.fehler);
            const fassungRef = hinweisFassungRef(bestand);
            if (!fassungRef) throw new Error('Der Hinweis hat keine Kennung — bitte neu laden und erneut versuchen.');

            const auftrag: VersandAuftrag = {
                art: 'bedenkenanzeige',
                projectId: bestand.project_id,
                planungId: bestand.planung_id,
                projektName: versand.projektName,
                empfaenger,
                betreff: mailBetreff.trim(),
                text: mailText,
                pdf: blob.wert,
                dateiname: hinweisDateiname(bestand, projektKennung, datumIso),
                titel: hinweisTitel(bestand),
                fassungRef,
                stand: `Stand ${datumDeutsch(datumIso)}`,
                // Der Riegel gilt zentral im Versandweg; der Dialog nennt ihn
                // zusaetzlich als Blocker, damit der Knopf nicht stumm bleibt.
                sperrgrund: versand.versandGesperrt ?? null,
            };
            const lauf = await fuehreHinweisLauf({
                hinweisId: bestand.id,
                stand: standGilt,
                bereitsGestempelt: bereitsVersendet,
                auftrag,
                schritte: schritteFuer(bestand),
            });
            setVersandStand(lauf.stand);

            if (!lauf.versand) {
                // Vorab-Stempel gescheitert — es ging nichts hinaus; zuerst nachholen.
                setMeldung({
                    art: 'warnung',
                    text: `Der Stempel „versendet" fehlt noch und konnte nicht gesetzt werden — bitte zuerst nachholen: ${lauf.stempel?.fehler || 'unbekannter Fehler'}`,
                    stempelNachholen: standGilt && standGilt.versendetAm ? { versendetAm: standGilt.versendetAm } : undefined,
                });
                return;
            }

            setMeldung(lauf.laufArt === 'nur_kopien' || !lauf.stempel
                ? deuteWiederholung(lauf.versand, lauf.stempel)
                : deuteErgebnis({ versand: lauf.versand, stempel: lauf.stempel }));
            if (lauf.uebernehmen) uebernimmStempel(lauf.uebernehmen);
            // Zu: alles erreicht und gestempelt. Sonst bleibt der Dialog mit
            // „Stempel nachholen" bzw. „erneut senden" stehen.
            const gestempelt = bereitsVersendet || !!lauf.uebernehmen;
            if (gestempelt && lauf.stand && !lauf.stand.nichtErreicht.length) setVersandOffen(false);
        } catch (e) {
            setMeldung({ art: 'fehler', text: e instanceof Error ? e.message : String(e) });
        } finally {
            versandRef.current = false;
            setVersandLaeuft(false);
        }
    };

    /** „Jetzt versenden": Bauherr plus Kopien — nur, solange noch niemand erreicht wurde. */
    const versenden = async () => {
        if (standGilt) return;
        const hauptEmail = entwurf.empfaenger_email.trim();
        await fuehreVersandAus([
            { name: entwurf.empfaenger_name.trim(), email: hauptEmail },
            ...kopienErgebnis.empfaenger.filter(k => k.email.toLowerCase() !== hauptEmail.toLowerCase()),
        ]);
    };

    /** „An die nicht erreichten erneut senden": nur die Adressen, die der letzte Lauf nicht erreicht hat. */
    const erneutSenden = async () => {
        if (!standGilt || !standGilt.nichtErreicht.length) return;
        await fuehreVersandAus(standGilt.nichtErreicht);
    };

    /**
     * „Stempel nachholen": die Mail ist raus, nur `versendet_am` fehlt — mit dem
     * Zeitpunkt der ersten Zustellung. Die Regel, WAS danach uebernommen wird
     * (bei `schon_versendet` nur der gelesene DB-Wert), steht in
     * `stempelNachholen` (pur, getestet) — nicht hier.
     */
    const holeStempelNach = async (versendetAmIso: string) => {
        if (gesperrt || !bestand || versandRef.current) return;
        versandRef.current = true;
        setVersandLaeuft(true);
        try {
            const { stempel, uebernehmen } = await stempelNachholen(versendetAmIso, schritteFuer(bestand));
            const satz = bedenkenStempelMeldung(stempel);
            if (satz.fehler) {
                setMeldung({ art: 'warnung', text: satz.fehler, stempelNachholen: { versendetAm: versendetAmIso } });
                return;
            }
            const nachsatz = stempel.status === 'schon_versendet' && !uebernehmen
                ? ' Das gespeicherte Versanddatum konnte gerade nicht gelesen werden — es gilt; nach dem Neuladen steht es hier.'
                : '';
            setMeldung({ art: 'ok', text: `${satz.text || 'Stempel gesetzt.'}${nachsatz}` });
            if (uebernehmen) uebernimmStempel(uebernehmen);
            if (!standGilt || !standGilt.nichtErreicht.length) setVersandOffen(false);
        } finally {
            versandRef.current = false;
            setVersandLaeuft(false);
        }
    };

    const sperrTitel = isReadOnly ? readOnlyTitle('Die Bedenkenanzeige wird nicht versendet.') : undefined;
    const kontextTitel = kontextFehlt ? 'Dafür fehlen dem Editor die Projekt- und Firmendaten.' : undefined;

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl">

                <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${stil.farbe}`}>
                        {stil.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-slate-800">
                            {bestand ? `${HINWEIS_ART_LABEL[entwurf.art]} Nr. ${bestand.nummer}` : HINWEIS_ART_LABEL[entwurf.art]}
                        </h3>
                        <p className="text-[11px] text-slate-500">{stil.erklaerung}</p>
                    </div>
                    <button type="button" onClick={onAbbrechen} className="p-2 text-slate-400 hover:text-slate-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4">

                    {bereitsVersendet && (
                        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                            <span className="text-xs text-emerald-800 leading-snug">
                                Am {String(versendetAm).slice(0, 10)} versendet. Das Versanddatum bleibt
                                stehen — für den Zugangsnachweis zählt der erste Versand.
                            </span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Feld label="Art">
                            <select
                                value={entwurf.art}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('art', e.target.value as HinweisArt)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            >
                                {(['hinweis', 'bedenken', 'gefahr'] as HinweisArt[]).map(a => (
                                    <option key={a} value={a}>{HINWEIS_ART_LABEL[a]}</option>
                                ))}
                            </select>
                        </Feld>
                        <div className="sm:col-span-2">
                            <Feld label="Betreff" pflicht>
                                <input
                                    type="text"
                                    value={entwurf.betreff}
                                    disabled={isReadOnly || bereitsVersendet}
                                    onChange={e => setze('betreff', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                    placeholder="Worum geht es?"
                                />
                            </Feld>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Feld label="Empfänger" pflicht hinweis="Der Bauherr bzw. der nach § 4 BaustellV Verantwortliche.">
                            <input
                                type="text"
                                value={entwurf.empfaenger_name}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('empfaenger_name', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </Feld>
                        <Feld label="E-Mail des Empfängers" hinweis="Für den Versand aus der App Pflicht — ohne Adresse kein Mailnachweis.">
                            <input
                                type="email"
                                value={entwurf.empfaenger_email}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('empfaenger_email', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </Feld>
                    </div>

                    {/* Vorbelegung: Bauherr aus Zuordnung bzw. Projektdaten — per Klick, nie still. */}
                    {!bereitsVersendet && !isReadOnly && vorbelegungVerfuegbar && !(entwurf.empfaenger_name || '').trim() && (
                        <button
                            type="button"
                            onClick={bauherrUebernehmen}
                            disabled={gesperrt}
                            className="text-xs font-bold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                        >
                            Bauherrn übernehmen: {vorbelegung.name}{vorbelegung.email ? ` (${vorbelegung.email})` : ''}
                            <span className="font-normal text-slate-500"> — {vorbelegung.grund}</span>
                        </button>
                    )}
                    {!bereitsVersendet && vorbelegt && (entwurf.empfaenger_name || '').trim() === vorbelegt.name && (
                        <p className="text-[11px] text-slate-500 -mt-2">
                            Empfänger vorbelegt ({vorbelegt.grund}) — bitte prüfen, änderbar.
                        </p>
                    )}

                    {entwurf.art !== 'hinweis' && (
                        <label className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                            entwurf.adressat_ist_bauherr
                                ? 'bg-emerald-50 border-emerald-200'
                                : 'bg-rose-50 border-rose-300'
                        }`}>
                            <input
                                type="checkbox"
                                checked={entwurf.adressat_ist_bauherr}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('adressat_ist_bauherr', e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded border-slate-300"
                            />
                            <span className="text-xs leading-snug text-slate-700">
                                <strong>Der Empfänger ist der Bauherr selbst</strong> bzw. der nach § 4 BaustellV
                                Verantwortliche und zur Entscheidung befugt. — Eine Anzeige, die nur den Architekten
                                erreicht, enthaftet regelmäßig nicht.
                            </span>
                        </label>
                    )}

                    <Feld
                        label="Sachverhalt"
                        pflicht={entwurf.art !== 'hinweis'}
                        hinweis="Was liegt konkret vor? Bezug auf Pläne, Termine, Unterlagen — nicht allgemein."
                    >
                        <textarea
                            value={entwurf.sachverhalt}
                            disabled={isReadOnly || bereitsVersendet}
                            onChange={e => setze('sachverhalt', e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                        />
                    </Feld>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Feld label="Gefährdung" pflicht={entwurf.art !== 'hinweis'} hinweis="Wer wird wodurch gefährdet?">
                            <textarea
                                value={entwurf.gefaehrdung}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('gefaehrdung', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </Feld>
                        <Feld
                            label="Konkrete nachteilige Folgen"
                            pflicht={entwurf.art !== 'hinweis'}
                            hinweis="Der wichtigste Teil. Ohne benannte Folgen ist die Anzeige unwirksam."
                        >
                            <textarea
                                value={entwurf.folgen}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('folgen', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-amber-300 bg-amber-50/40 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </Feld>
                    </div>

                    <Feld
                        label={entwurf.art === 'gefahr' ? 'Verlangen (Einstellung der Arbeiten)' : 'Geforderte Entscheidung oder Abhilfe'}
                        pflicht={entwurf.art !== 'hinweis'}
                    >
                        <textarea
                            value={entwurf.forderung}
                            disabled={isReadOnly || bereitsVersendet}
                            onChange={e => setze('forderung', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                        />
                    </Feld>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Feld label="Frist zur Rückäußerung" pflicht={entwurf.art !== 'hinweis'}>
                            <input
                                type="date"
                                value={String(entwurf.frist_am || '').slice(0, 10)}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('frist_am', e.target.value || null)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </Feld>
                        <Feld label="Folge bei Fristablauf" pflicht={entwurf.art !== 'hinweis'} hinweis="Was geschieht, wenn keine Reaktion kommt?">
                            <input
                                type="text"
                                value={entwurf.folge_bei_fristablauf}
                                disabled={isReadOnly || bereitsVersendet}
                                onChange={e => setze('folge_bei_fristablauf', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                placeholder="z. B. erneute Anzeige mit verschärfter Frist"
                            />
                        </Feld>
                    </div>

                    {(entwurf.frage_codes || []).length > 0 && (
                        <div className="text-[11px] text-slate-500">
                            Bezug auf {entwurf.frage_codes.length} Frage(n):{' '}
                            <span className="font-mono">{entwurf.frage_codes.join(', ')}</span>
                        </div>
                    )}

                    {bestand && (
                        <Feld
                            label="Reaktion des Bauherrn"
                            hinweis="Trifft der Bauherr trotz Anzeige eine risikoerhöhende Entscheidung, ist DIE ENTSCHEIDUNG zu dokumentieren — nicht nur der Hinweis."
                        >
                            <textarea
                                value={entwurf.reaktion}
                                disabled={isReadOnly}
                                onChange={e => setze('reaktion', e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </Feld>
                    )}

                    {versandVersucht && maengel.length > 0 && !versandOffen && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 text-xs font-bold text-rose-700 mb-1">
                                <AlertTriangle className="w-4 h-4" /> So ist die Anzeige nicht wirksam:
                            </div>
                            <ul className="list-disc list-inside space-y-0.5">
                                {maengel.map((m, i) => (
                                    <li key={i} className="text-[11px] text-rose-700 leading-snug">{m}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* ─── Versanddialog (im Editor, kein Browser-Dialog) ───────── */}
                    {versandOffen && versand && (
                        <div className="border border-blue-200 bg-blue-50/40 rounded-xl p-4 space-y-3">
                            <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                                <Mail className="w-4 h-4 text-blue-600" />
                                Versenden: PDF in die Ablage und per E-Mail an den Bauherrn
                            </div>
                            <p className="text-[11px] text-slate-600 leading-snug">
                                Reihenfolge: PDF erzeugen, in der Projektablage ablegen, E-Mail mit Anhang
                                versenden — erst nach Erfolg wird der Hinweis als versendet gestempelt. Das
                                Blatt entsteht aus dem gespeicherten Satz.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Feld label="An" hinweis={'Der Empfänger steht am Hinweis (Feld „Empfänger" oben) und wird mit ihm gespeichert.'}>
                                    <div className="px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm text-slate-700">
                                        {(entwurf.empfaenger_name || '').trim() || <span className="text-rose-600">Empfänger fehlt</span>}
                                        <span className="block text-xs text-slate-500">
                                            {(entwurf.empfaenger_email || '').trim() || 'E-Mail-Adresse fehlt'}
                                        </span>
                                    </div>
                                </Feld>
                                <Feld label="Kopie an" hinweis={'Je Zeile eine Adresse, wahlweise „Name <adresse>". Freiwillig.'}>
                                    <textarea
                                        value={kopien}
                                        onChange={e => setKopien(e.target.value)}
                                        disabled={versandLaeuft}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                    />
                                </Feld>
                            </div>

                            <Feld label="Betreff der E-Mail" pflicht>
                                <input
                                    type="text"
                                    value={mailBetreff}
                                    onChange={e => setMailBetreff(e.target.value)}
                                    disabled={versandLaeuft}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                />
                            </Feld>
                            <Feld label="Text der E-Mail" hinweis="Vorbelegt; der Inhalt der Anzeige steht im angehängten PDF.">
                                <textarea
                                    value={mailText}
                                    onChange={e => setMailText(e.target.value)}
                                    disabled={versandLaeuft}
                                    rows={7}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                />
                            </Feld>

                            {versandBlocker.length > 0 && (
                                <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                                    <div className="flex items-center gap-2 text-xs font-bold text-rose-700 mb-1">
                                        <AlertTriangle className="w-4 h-4" /> Noch nicht versandfähig:
                                    </div>
                                    <ul className="list-disc list-inside space-y-0.5">
                                        {versandBlocker.map((b, i) => (
                                            <li key={i} className="text-[11px] text-rose-700 leading-snug">{b}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Sperre nach einem Lauf mit Erfolg: kein zweiter Versand an alle. */}
                            {standGilt && (
                                <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-[11px] text-amber-800 leading-snug">
                                    {standGilt.versendetAm ? (
                                        <>
                                            <strong>Bereits versendet</strong> am {datumDeutsch(standGilt.versendetAm) || 'heute'} an{' '}
                                            {standGilt.erreicht.join(', ')}. Ein erneuter Versand an alle ist gesperrt.
                                        </>
                                    ) : (
                                        <>
                                            <strong>Bauherr nicht erreicht — nichts gestempelt.</strong> Hinausgegangen sind nur
                                            Kopien an {standGilt.erreicht.join(', ')}; an sie geht nichts noch einmal.
                                        </>
                                    )}
                                    {standGilt.nichtErreicht.length > 0 && (
                                        <> Nicht erreicht: {standGilt.nichtErreicht.map(e => e.email).join(', ')}.</>
                                    )}
                                    {standGilt.versendetAm && !bereitsVersendet && <> Der Stempel „versendet" fehlt noch.</>}
                                </div>
                            )}

                            <div className="flex items-center gap-2 justify-end flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setVersandOffen(false)}
                                    disabled={versandLaeuft}
                                    className="px-3 py-2 text-sm font-bold text-slate-600 hover:text-slate-800 disabled:opacity-50"
                                >
                                    {standGilt ? 'Schließen' : 'Abbrechen'}
                                </button>
                                {!standGilt && (
                                    <button
                                        type="button"
                                        onClick={() => { void versenden(); }}
                                        disabled={gesperrt || versandBlocker.length > 0}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        <Send className="w-4 h-4" />
                                        {versandLaeuft ? 'Wird versendet …' : 'Jetzt versenden'}
                                    </button>
                                )}
                                {standGilt && standGilt.versendetAm && !bereitsVersendet && (
                                    <button
                                        type="button"
                                        onClick={() => { void holeStempelNach(standGilt.versendetAm as string); }}
                                        disabled={gesperrt}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                                    >
                                        <Stamp className="w-4 h-4" />
                                        Stempel nachholen
                                    </button>
                                )}
                                {standGilt && standGilt.nichtErreicht.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => { void erneutSenden(); }}
                                        disabled={gesperrt || versandBlocker.length > 0}
                                        title={standGilt.nichtErreicht.map(e => e.email).join(', ')}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        <Send className="w-4 h-4" />
                                        {versandLaeuft ? 'Wird versendet …' : `An die nicht erreichten erneut senden (${standGilt.nichtErreicht.length})`}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {meldung && (
                        <div className={`flex items-start gap-2 rounded-lg px-3 py-2 border ${
                            meldung.art === 'ok'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                : meldung.art === 'warnung'
                                    ? 'bg-amber-50 border-amber-300 text-amber-800'
                                    : 'bg-rose-50 border-rose-200 text-rose-700'
                        }`}>
                            {meldung.art === 'ok'
                                ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                            <div className="text-xs leading-snug flex-1">
                                {meldung.text}
                                {meldung.stempelNachholen && !bereitsVersendet && (
                                    <button
                                        type="button"
                                        onClick={() => { void holeStempelNach(meldung.stempelNachholen!.versendetAm); }}
                                        disabled={gesperrt}
                                        className="ml-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-bold text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50"
                                    >
                                        <Stamp className="w-3 h-3" /> Stempel nachholen
                                    </button>
                                )}
                            </div>
                            <button type="button" onClick={() => setMeldung(null)} className="text-current opacity-60 hover:opacity-100">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            type="button"
                            onClick={onAbbrechen}
                            className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-800"
                        >
                            Schließen
                        </button>
                        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
                            <button
                                type="button"
                                onClick={() => { void pdfAnsehen(); }}
                                disabled={gesperrt || kontextFehlt || ungespeichert}
                                title={sperrTitel || kontextTitel || (ungespeichert ? ERST_SPEICHERN : 'Das Blatt aus dem gespeicherten Satz.')}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                            >
                                <FileText className="w-4 h-4" />
                                PDF ansehen
                            </button>
                            <button
                                type="button"
                                onClick={onSpeichern}
                                disabled={gesperrt}
                                className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                            >
                                Als Entwurf speichern
                            </button>
                            {!bereitsVersendet && (
                                <button
                                    type="button"
                                    onClick={oeffneVersand}
                                    disabled={gesperrt || kontextFehlt || versandOffen}
                                    title={sperrTitel || kontextTitel || 'PDF in die Ablage und per E-Mail an den Bauherrn; danach Stempel.'}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <Send className="w-4 h-4" />
                                    Versenden …
                                </button>
                            )}
                        </div>
                    </div>
                    {ungespeichert && !kontextFehlt && !isReadOnly && (
                        <p className="text-[11px] text-amber-700 leading-snug">
                            {bestand ? 'Ungespeicherte Änderungen' : 'Noch nicht gespeichert'} — „PDF ansehen" und „Jetzt versenden" erst
                            nach „Als Entwurf speichern". Gedruckt wird nur, was die Akte trägt.
                        </p>
                    )}
                    {!bereitsVersendet && (
                        <div className="flex items-start gap-2 text-[11px] text-slate-500 leading-snug">
                            <Stamp className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span className="flex-1">
                                Außerhalb der App versendet (Brief, eigenes Mailprogramm)?{' '}
                                <button
                                    type="button"
                                    onClick={stempeln}
                                    disabled={gesperrt}
                                    title={sperrTitel}
                                    className="font-bold text-slate-700 underline decoration-dotted hover:text-slate-900 disabled:opacity-50"
                                >
                                    {laeuft ? 'Wird festgehalten …' : 'Als versendet festhalten'}
                                </button>
                                {' '}— setzt nur den Stempel. Ablage und Mailnachweis in der App fehlen dann; ein
                                Entwurf ohne Stempel deckt keine offene Frage.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
